import React, { useEffect, useState } from "react";
import {
    Box,
    TextField,
    Typography,
    Paper,
    Grid,
    Button,
    Checkbox,
    FormControlLabel,
    Select,
    MenuItem,
    InputLabel,
    FormControl,
    IconButton,
    Divider,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import UpdateIcon from "@mui/icons-material/Update";
import { Link } from "react-router-dom";
import { getCodexFromLocation } from "../timeserver/uselivedata";
import { authHeader } from "../auth";

// Si le frontend tourne sur un port différent du serveur Node en dev
// (ex: CRA sur :3000, server.js sur :3001), un fetch relatif "/api/..."
// part vers le port 3000 et retombe forcément en 404. Renseigne
// REACT_APP_API_BASE=http://localhost:3001 dans le .env pour ce cas.
// En production (frontend servi par server.js lui-même), laisse vide.
let API_BASE = process.env.REACT_APP_API_BASE || "";

// Garde-fou : si API_BASE est renseigné mais ne ressemble pas à une URL
// (http:// ou https://), c'est presque certainement une erreur de copier-
// coller dans le .env (ex: la clé API mise ici par erreur). Une valeur
// invalide ici produit une URL relative cassée, résolue n'importe comment
// par le navigateur — donc on l'ignore plutôt que de laisser planter
// silencieusement toutes les requêtes.
if (API_BASE && !/^https?:\/\//i.test(API_BASE)) {
    console.error(
        `⚠ REACT_APP_API_BASE="${API_BASE}" ne ressemble pas à une URL valide ` +
        `(doit commencer par http:// ou https://, ou être vide en production). ` +
        `Valeur ignorée — vérifie ton .env frontend.`
    );
    API_BASE = "";
}

export default function Config() {
    const codex = getCodexFromLocation();

    const [eventTitle, setEventTitle] = useState("");
    const [eventDate, setEventDate] = useState("");
    const [logoLeft, setLogoLeft] = useState("");
    const [logoRight, setLogoRight] = useState("");
    const [showTicker, setShowTicker] = useState(false);
    const [tickerMessage, setTickerMessage] = useState("");
    const [speakerCode, setSpeakerCode] = useState("");
    const [categories, setCategories] = useState([]);
    const [connectionMsg, setConnectionMsg] = useState("");

    useEffect(() => {
        if (!codex) return;
        fetch(`${API_BASE}/api/config/${encodeURIComponent(codex)}`)
            .then((r) => (r.ok ? r.json() : {}))
            .then((cfg) => {
                setEventTitle(cfg.eventTitle || "");
                setEventDate(cfg.eventDate || "");
                setLogoLeft(cfg.logoLeft || "");
                setLogoRight(cfg.logoRight || "");
                setShowTicker(cfg.showTicker || false);
                setTickerMessage(cfg.tickerMessage || "");
                setSpeakerCode(cfg.speakerCode || "");
                setCategories(
                    (cfg.categories || []).map((c, i) => ({
                        id: c.id ?? `${Date.now()}-${i}`,
                        name: c.name || "",
                        sex: c.sex || "ALL",
                        yearMin: c.yearMin ?? "",
                        yearMax: c.yearMax ?? "",
                    }))
                );
            })
            .catch(() => {});
    }, [codex]);

    const addCategory = () => {
        setCategories((prev) => [
            ...prev,
            {
                id: `${Date.now()}-${Math.random()}`,
                name: "",
                sex: "ALL",
                yearMin: "",
                yearMax: "",
            },
        ]);
    };

    const updateCategory = (id, field, value) => {
        setCategories((prev) =>
            prev.map((c) => (c.id === id ? { ...c, [field]: value } : c))
        );
    };

    const removeCategory = (id) => {
        setCategories((prev) => prev.filter((c) => c.id !== id));
    };

    // Téléchargement d'un fichier JSON contenant les catégories actuelles
    // (sans le champ "id", purement local à cette page) — pensé pour être
    // réimporté sur une AUTRE page de config (autre codex, saison
    // suivante) via le bouton Importer ci-dessous.
    const exportCategories = () => {
        if (categories.length === 0) {
            setConnectionMsg("⚠ Aucune catégorie à exporter.");
            return;
        }
        const payload = categories.map(({ name, sex, yearMin, yearMax }) => ({ name, sex, yearMin, yearMax }));
        const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `categories_${codex || "export"}.json`;
        a.click();
        URL.revokeObjectURL(url);
    };

    // Réimporte un fichier exporté par le bouton ci-dessus — REMPLACE la
    // liste actuelle (pas de fusion, pour éviter les doublons silencieux).
    const importCategories = (file) => {
        if (!file) return;
        const reader = new FileReader();
        reader.onload = () => {
            try {
                const data = JSON.parse(reader.result);
                if (!Array.isArray(data)) throw new Error("Le fichier ne contient pas une liste de catégories.");
                setCategories(
                    data.map((c, i) => ({
                        id: `${Date.now()}-${i}`,
                        name: c.name || "",
                        sex: c.sex || "ALL",
                        yearMin: c.yearMin ?? "",
                        yearMax: c.yearMax ?? "",
                    }))
                );
                setConnectionMsg(`✅ ${data.length} catégorie(s) importée(s) — pense à sauvegarder.`);
            } catch (err) {
                setConnectionMsg("❌ Fichier de catégories invalide : " + err.message);
            }
        };
        reader.readAsText(file);
    };

    // Décale yearMin/yearMax de +1 sur TOUTES les catégories actuellement
    // affichées (ex: 1990-1997 -> 1991-1998) — pour reconduire les mêmes
    // catégories d'âge d'une saison à l'autre sans tout retaper à la main.
    // Un champ vide (pas de restriction sur ce critère) reste vide.
    // N'enregistre rien tout seul — reste à cliquer "Sauvegarder" ensuite.
    const shiftCategoriesByOneYear = () => {
        if (categories.length === 0) {
            setConnectionMsg("⚠ Aucune catégorie à décaler.");
            return;
        }
        setCategories((prev) =>
            prev.map((c) => ({
                ...c,
                yearMin: c.yearMin === "" || c.yearMin === null ? "" : Number(c.yearMin) + 1,
                yearMax: c.yearMax === "" || c.yearMax === null ? "" : Number(c.yearMax) + 1,
            }))
        );
        setConnectionMsg("✅ Catégories décalées d'une année — pense à sauvegarder.");
    };

    // Garde-fou simple : les logos sont stockés en base64 directement dans
    // la config (pas de stockage fichier côté serveur), donc on évite les
    // images trop lourdes pour ne pas gonfler inutilement la config.
    const MAX_LOGO_SIZE = 2 * 1024 * 1024; // 2 Mo

    const handleLogoUpload = (file, setter) => {
        if (!file) return;
        if (file.size > MAX_LOGO_SIZE) {
            setConnectionMsg("⚠ Image trop lourde (> 2 Mo) — choisis une image plus légère.");
            return;
        }
        const reader = new FileReader();
        reader.onload = () => setter(reader.result);
        reader.readAsDataURL(file);
    };

    const saveConfig = async () => {
        if (!codex) {
            setConnectionMsg(
                "❌ Aucun codex détecté dans l'URL (ouvre cette page via /live/<codex>/config)"
            );
            return;
        }
        try {
            const res = await fetch(`${API_BASE}/api/config/${encodeURIComponent(codex)}`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    ...authHeader(),
                },
                body: JSON.stringify({
                    eventTitle,
                    eventDate,
                    logoLeft,
                    logoRight,
                    showTicker,
                    tickerMessage,
                    speakerCode,
                    categories,
                }),
            });
            if (!res.ok) {
                let detail = "";
                try {
                    detail = await res.text();
                } catch {
                    /* ignore */
                }
                throw new Error(`HTTP ${res.status}${detail ? " — " + detail : ""}`);
            }
            setConnectionMsg("✅ Configuration sauvegardée");
        } catch (err) {
            setConnectionMsg("❌ Erreur de sauvegarde : " + err.message);
        }
    };

    return (
        <Box display="flex" flexDirection="column" alignItems="center" minHeight="90vh" mt={4}>
            <Paper elevation={4} sx={{ p: 4, width: "720px", maxWidth: "95vw" }}>
                <Typography variant="h4" align="center" gutterBottom>
                    Configuration de l'événement
                </Typography>

                {!codex && (
                    <Typography color="error" align="center" sx={{ mb: 2 }}>
                        Aucun codex détecté dans l'URL. Ouvre cette page via /live/&lt;codex&gt;/config
                    </Typography>
                )}

                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Titre affiché (laisser vide pour utiliser celui de SkiAlpPro)"
                            value={eventTitle}
                            onChange={(e) => setEventTitle(e.target.value)}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Date affichée (laisser vide pour utiliser celle de SkiAlpPro)"
                            value={eventDate}
                            onChange={(e) => setEventDate(e.target.value)}
                        />
                    </Grid>

                    <Grid item xs={6}>
                        <TextField
                            fullWidth
                            label="Logo gauche (URL ou image uploadée)"
                            value={logoLeft}
                            onChange={(e) => setLogoLeft(e.target.value)}
                        />
                        <Button
                            variant="outlined"
                            component="label"
                            size="small"
                            sx={{ mt: 1 }}
                        >
                            Choisir une image
                            <input
                                type="file"
                                accept="image/*"
                                hidden
                                onChange={(e) => handleLogoUpload(e.target.files?.[0], setLogoLeft)}
                            />
                        </Button>
                        {logoLeft && (
                            <Box mt={1} textAlign="center">
                                <img src={logoLeft} alt="Logo gauche" style={{ maxHeight: "80px" }} />
                            </Box>
                        )}
                    </Grid>

                    <Grid item xs={6}>
                        <TextField
                            fullWidth
                            label="Logo droit (URL ou image uploadée)"
                            value={logoRight}
                            onChange={(e) => setLogoRight(e.target.value)}
                        />
                        <Button
                            variant="outlined"
                            component="label"
                            size="small"
                            sx={{ mt: 1 }}
                        >
                            Choisir une image
                            <input
                                type="file"
                                accept="image/*"
                                hidden
                                onChange={(e) => handleLogoUpload(e.target.files?.[0], setLogoRight)}
                            />
                        </Button>
                        {logoRight && (
                            <Box mt={1} textAlign="center">
                                <img src={logoRight} alt="Logo droit" style={{ maxHeight: "80px" }} />
                            </Box>
                        )}
                    </Grid>

                    <Grid item xs={12}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={showTicker}
                                    onChange={(e) => setShowTicker(e.target.checked)}
                                />
                            }
                            label="Afficher un bandeau défilant"
                        />
                        {showTicker && (
                            <TextField
                                fullWidth
                                label="Message du bandeau"
                                value={tickerMessage}
                                onChange={(e) => setTickerMessage(e.target.value)}
                            />
                        )}
                    </Grid>

                    <Grid item xs={12}>
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="h6" sx={{ mb: 1 }}>
                            Vue speaker
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Code à communiquer au speaker pour accéder à /live/&lt;codex&gt;/speaker
                            (page publique, pas de vrai login — juste ce code manuel). Laisse vide
                            pour désactiver la protection (page accessible sans code).
                        </Typography>
                        <TextField
                            fullWidth
                            label="Code speaker"
                            value={speakerCode}
                            onChange={(e) => setSpeakerCode(e.target.value)}
                            sx={{ maxWidth: 300 }}
                        />
                        <Divider sx={{ my: 2 }} />
                    </Grid>

                    <Grid item xs={12}>
                        <Divider sx={{ my: 1 }} />
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                flexWrap: "wrap",
                                gap: 1,
                                mb: 1,
                            }}
                        >
                            <Typography variant="h6">Catégories (filtre résultats / liste de départ)</Typography>
                            <Box sx={{ display: "flex", gap: 1 }}>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<FileDownloadIcon />}
                                    onClick={exportCategories}
                                >
                                    Exporter
                                </Button>
                                <Button size="small" variant="outlined" component="label" startIcon={<FileUploadIcon />}>
                                    Importer
                                    <input
                                        type="file"
                                        accept="application/json"
                                        hidden
                                        onChange={(e) => importCategories(e.target.files?.[0])}
                                    />
                                </Button>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    color="secondary"
                                    startIcon={<UpdateIcon />}
                                    onClick={shiftCategoriesByOneYear}
                                >
                                    Décaler d'un an
                                </Button>
                            </Box>
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Définis ici les catégories de la course (ex: U14, U16, Dames Elite...).
                            Elles apparaîtront comme filtre sur les pages Live et Startlist.
                            Laisse Année min/max vide si pas de restriction sur ce critère.
                            « Exporter » télécharge la liste actuelle (réutilisable via « Importer »
                            sur une autre course) ; « Décaler d'un an » ajoute 1 à toutes les années
                            min/max déjà saisies (ex: 1990-1997 devient 1991-1998), pratique pour
                            reconduire les mêmes catégories d'âge à la saison suivante.
                        </Typography>

                        {categories.map((cat) => (
                            <Box
                                key={cat.id}
                                sx={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: 1,
                                    alignItems: "center",
                                    mb: 1.5,
                                }}
                            >
                                <TextField
                                    label="Nom"
                                    size="small"
                                    value={cat.name}
                                    onChange={(e) => updateCategory(cat.id, "name", e.target.value)}
                                    sx={{ width: 140 }}
                                />
                                <FormControl size="small" sx={{ width: 130 }}>
                                    <InputLabel>Sexe</InputLabel>
                                    <Select
                                        label="Sexe"
                                        value={cat.sex}
                                        onChange={(e) => updateCategory(cat.id, "sex", e.target.value)}
                                    >
                                        <MenuItem value="ALL">Tous</MenuItem>
                                        <MenuItem value="HOMME">Hommes</MenuItem>
                                        <MenuItem value="FEMME">Femmes</MenuItem>
                                    </Select>
                                </FormControl>
                                <TextField
                                    label="Année min"
                                    size="small"
                                    type="number"
                                    value={cat.yearMin}
                                    onChange={(e) => updateCategory(cat.id, "yearMin", e.target.value)}
                                    sx={{ width: 110 }}
                                />
                                <TextField
                                    label="Année max"
                                    size="small"
                                    type="number"
                                    value={cat.yearMax}
                                    onChange={(e) => updateCategory(cat.id, "yearMax", e.target.value)}
                                    sx={{ width: 110 }}
                                />
                                <IconButton
                                    aria-label="Supprimer la catégorie"
                                    color="error"
                                    onClick={() => removeCategory(cat.id)}
                                >
                                    <DeleteIcon />
                                </IconButton>
                            </Box>
                        ))}

                        <Button variant="outlined" onClick={addCategory} sx={{ mt: 1 }}>
                            + Ajouter une catégorie
                        </Button>
                        <Divider sx={{ my: 2 }} />
                    </Grid>

                    <Grid item xs={12}>
                        <Button variant="contained" color="secondary" onClick={saveConfig}>
                            Sauvegarder la configuration
                        </Button>
                    </Grid>

                    {codex && (
                        <Grid item xs={12}>
                            <Button component={Link} to={`/live/${codex}`} variant="outlined" color="primary">
                                Voir le live
                            </Button>
                        </Grid>
                    )}
                </Grid>
            </Paper>

            {connectionMsg && (
                <Box mt={3}>
                    <Typography variant="body1" color="info.main" align="center">
                        {connectionMsg}
                    </Typography>
                </Box>
            )}
        </Box>
    );
}